import { useLocation, useSearchParams } from "react-router-dom";
import { ReactNode } from "react";
import NoResultsTemplate from "./NoResultsTemplate.tsx";
import { useIntl } from "react-intl";
import PaddedDotLoader from "./PaddedDotLoader.tsx";
import { getSearchQuery } from "../query.ts";
import BaseLinkOrButton from "../../../../common/core-ui/BaseLinkOrButton.tsx";

export default function ResultsTemplate({
  isLoading,
  isEmpty,
  children,
}: {
  isLoading: boolean;
  isEmpty: boolean;
  children: ReactNode;
}) {
  const intl = useIntl();
  const location = useLocation();
  const [params] = useSearchParams();
  const query = getSearchQuery(params);

  if (isLoading) {
    return <PaddedDotLoader />;
  }

  const makeLink = (chunks: ReactNode, route: string) => (
    <BaseLinkOrButton className="vst-link" to={route + location.search}>
      {chunks}
    </BaseLinkOrButton>
  );

  const strings: Record<string, { title: string; message: ReactNode }> = {
    "/search/library/titles": {
      title: intl.formatMessage(
        {
          defaultMessage: "We couldn’t find a title match for “{query}”",
        },
        { query }
      ),
      message: intl.formatMessage(
        {
          defaultMessage:
            "Check results in the <a>Book Content</a> tab, or try using a different search term",
        },
        {
          a: (chunks: ReactNode) =>
            makeLink(chunks, "/search/library/book-content"),
        }
      ),
    },
    "/search/library/book-content": {
      title: intl.formatMessage(
        {
          defaultMessage: "We couldn’t find a content match for “{query}”",
        },
        { query }
      ),
      message: intl.formatMessage(
        {
          defaultMessage:
            "Check results in the <a>Titles tab</a>, or try using a different search term",
        },
        {
          a: (chunks: ReactNode) => makeLink(chunks, "/search/library/titles"),
        }
      ),
    },
  };

  if (isEmpty) {
    const stringsToUse = strings[location.pathname] || {};
    return (
      <NoResultsTemplate
        title={stringsToUse.title}
        message={stringsToUse.message}
      />
    );
  }

  return children;
}
